import { z } from 'zod';

/**
 * The command envelope (ARCHITECTURE §4/§6). Every mutation — from the web app
 * and from the offline scanner outbox alike — is one of these. `commandId` is
 * client-generated and makes replays no-ops via the `command_dedupe` table.
 */
export const commandEnvelopeSchema = z.object({
  commandId: z.uuid(),
  /** dot-namespaced handler key, e.g. `move.pallet`, `pick.confirm`. */
  type: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z][a-zA-Z0-9]*(\.[a-z][a-zA-Z0-9]*)+$/, 'command type must be dot.namespaced'),
  payload: z.unknown(),
  /**
   * Context the device actually observed (scanned bin, counted qty, snapshot
   * age). The server validates the payload against CURRENT state using this —
   * apply / auto-heal / reject. Never last-write-wins.
   */
  observed: z.record(z.string(), z.unknown()).nullish(),
  /** Scanner device UUID; absent for web-originated commands. */
  deviceId: z.uuid().nullish(),
  /** Client clock at enqueue time — diagnostic only, never authoritative. */
  at: z.iso.datetime().optional(),
});
export type CommandEnvelope<P = unknown> = Omit<z.infer<typeof commandEnvelopeSchema>, 'payload'> & {
  payload: P;
};

export const commandVerdictSchema = z.enum(['applied', 'healed', 'rejected']);
export type CommandVerdict = z.infer<typeof commandVerdictSchema>;

export const commandResultSchema = z.object({
  commandId: z.uuid(),
  verdict: commandVerdictSchema,
  /** True when the command had already been applied (idempotent replay). */
  replayed: z.boolean().default(false),
  /** Handler return value, echoed back so the device can reconcile its overlay. */
  result: z.unknown().optional(),
  /** i18n error code, present iff verdict === 'rejected'. */
  code: z.string().optional(),
  params: z.record(z.string(), z.unknown()).optional(),
  /** What the server changed when it auto-healed, for the on-device banner. */
  healing: z.record(z.string(), z.unknown()).optional(),
});
export type CommandResult<R = unknown> = Omit<z.infer<typeof commandResultSchema>, 'result'> & {
  result?: R;
};

/** POST /api/sync/commands — the scanner outbox drain endpoint (FIFO batch). */
export const syncCommandsRequestSchema = z.object({
  commands: z.array(commandEnvelopeSchema).min(1).max(200),
});
export type SyncCommandsRequest = z.infer<typeof syncCommandsRequestSchema>;

export const syncCommandsResponseSchema = z.object({
  results: z.array(commandResultSchema),
});
export type SyncCommandsResponse = z.infer<typeof syncCommandsResponseSchema>;

export const syncRejectionStatusSchema = z.enum(['open', 'resolved']);
export type SyncRejectionStatus = z.infer<typeof syncRejectionStatusSchema>;
